/**
 * Validates all required dropdown inputs within a specified section.
 *
 * Finds all dropdown elements marked as required (`aria-required="true"`) 
 * within a given section of an accordion. 
 * It then checks if each dropdown has a valid selection using the `isDropdownInputValid` function.
 *
 * @param {string} sectionId - The CSS selector for the section containing the dropdowns.
 * @returns {boolean} - Returns `true` if all required dropdowns in the section are valid, otherwise `false`.
 */
export const allDropdownsInSectionValid = (sectionId) => {
    // Collect all dropdowns in a section of the accordion with the "aria-required" attribute
    const dropdownsWrapperEls = document.querySelectorAll(sectionId + ' .atb-dropdown[aria-required="true"]');
    const dropdownValidity = Array.from(dropdownsWrapperEls)
        .filter(dropdown => dropdown.offsetParent !== null)
        .filter(dropdown => !dropdown.querySelector('input[data-is-js-template]'))
        .map(dropdown => isDropdownInputValid(dropdown));
    
    // Run all of the validation logic
    return dropdownValidity.every(isValid => isValid);
}


/**
 * Validates a custom dropdown input field inside a wrapper element.
 *
 * If the input is empty, it marks both the wrapper and the 
 * input as invalid using Bootstrap's `is-invalid` class. Otherwise, it removes 
 * any existing validation error styles.
 *
 * @param {HTMLElement} dropdownWrapperEl - The wrapper element containing the custom dropdown input.
 * @returns - Returns `true` if the input has a valid value (non-empty), otherwise `false`.
 */
export const isDropdownInputValid = (dropdownWrapperEl) => {
    const inputElement = dropdownWrapperEl.querySelector('input');
    const validationMessage = dropdownWrapperEl.parentElement?.querySelector('.atb-validation-message.invalid-feedback');

    // If pseudo input element we're using for the custom dropdown has no text in it, it's invalid
    const isValid = !(inputElement.value.trim() == "");

    if (!isValid) {
        // Add is-invalid to both the container and the input itself, so that we get the bootstrap validation label 
        dropdownWrapperEl.classList.add("is-invalid");
        inputElement.classList.add("is-invalid");
    } else {
        dropdownWrapperEl.classList.remove("is-invalid");
        inputElement.classList.remove("is-invalid");   
    }
    setControlValidationState(inputElement, validationMessage, isValid);
    return isValid;
}

export const setControlValidationState = (controlEl, validationMessageEl, isValid) => {
    if (!controlEl) return;

    controlEl.setAttribute('aria-invalid', isValid ? 'false' : 'true');

    if (!validationMessageEl) {
        if (isValid) controlEl.removeAttribute('aria-describedby');
        return;
    }

    if (!validationMessageEl.id && controlEl.id) {
        validationMessageEl.id = `${controlEl.id}-invalid`;
    }

    validationMessageEl.setAttribute('role', 'alert');
    validationMessageEl.setAttribute('aria-live', 'polite');

    if (!isValid && validationMessageEl.id) {
        controlEl.setAttribute('aria-describedby', validationMessageEl.id);
    } else {
        controlEl.removeAttribute('aria-describedby');
    }
}


export const checkPhoneInputs = (ref) => {
    const stage = document.querySelector(ref)
    const tels = stage.querySelectorAll('input[type="tel"]');
   
    let allTelsValid = true;

    tels.forEach(input => {
        if (!validatePhone(input)) {
            allTelsValid = false;
        }
    });

    return allTelsValid;
}

/**
 * Check if the total number of party members in a booking exceeds the max occupancy of a unit
 * @param {number} unitId - The ID of the unit to check
 * @returns {boolean} - whether or not the number of party members exceeds max occupancy for the given unit
 */
export const doPartyMembersExceedMax = (unit, partyMembers) => {
    // Find the unit
    if (!unit) return false; // return false early

    const maxOccupancy = parseIntb10(unit.occupancy) ?? Infinity;

    // Sum the quantities
    const totalQty = Object
                        .values(partyMembers)
                        .reduce((sum, value) => sum + parseIntb10(value), 0);

    return totalQty > maxOccupancy;
}

export const isWithinOccupancyRange = (numberOfPartyMembers = 0, unit) => {
    // return false early if no unit data was passed
    if (!unit) return false; 

    const maxOccupancy = parseIntb10(unit.occupancy) ?? Infinity;
    const minOccupancy = parseIntb10(unit.min_occupancy) ?? 1;

    return numberOfPartyMembers <= maxOccupancy && numberOfPartyMembers >= minOccupancy;
}

export const isStartEndDateSelected = () => {
    const periodInput = document.getElementById('atb-period');
    return periodInput ? parseIntb10(periodInput.value) > 0 : false;
}

// intlTelInput has some number validation to tap into
export const initPhoneInput = (input) => {
    if (!input._iti) { // only init if not already done
        input._iti = window.intlTelInput(input, {
            initialCountry: 'auto',
            separateDialCode: true
        });
    }
};

export const validatePhone = (phoneInput)  =>{
    
    initPhoneInput(phoneInput);

    const iti = phoneInput._iti;
    const valid = iti && iti.isValidNumber();

    phoneInput.classList.toggle('is-invalid', !valid);
    phoneInput.classList.toggle('is-valid', valid);
    const validationMessage = phoneInput
        .closest('.input-wrapper-row, .form-check, .col-12')
        ?.querySelector('.atb-validation-message.invalid-feedback');
    setControlValidationState(phoneInput, validationMessage, valid);

    return valid;
}
